import { useCallback, useMemo } from "react";

import { Trans, useTranslation } from "react-i18next";

import Button from "../../../../button";
import Loading from "../../../../loading";

import {
  isEmpty,
  isEmptyList,
  isEmptyObject,
  equalsIgnoringCase,
} from "../../../../../utils/validators";

function ProPlanCard(props) {
  const { loading, plan, showRegister, onRegister, showChange, onChange } =
    props;

  const { t, i18n } = useTranslation();

  const title = useMemo(() => {
    return t(`plans.pro.title`);
  }, [t]);

  const formattedPrice = useMemo(() => {
    if (
      !isEmptyObject(plan) &&
      !isEmpty(plan.currency) &&
      !isEmpty(plan.unit_amount_decimal)
    ) {
      const formatter = new Intl.NumberFormat(i18n.resolvedLanguage, {
        style: "currency",
        currencyDisplay: "symbol",
        currency: String(plan.currency).toUpperCase(),
      });
      return formatter.formatToParts(parseInt(plan.unit_amount_decimal) / 100);
    }
    return null;
  }, [plan, i18n.resolvedLanguage]);

  const priceInteger = useMemo(() => {
    if (!isEmptyObject(formattedPrice)) {
      const part = formattedPrice.find((i) =>
        equalsIgnoringCase("integer", i.type)
      );
      if (!isEmptyObject(part)) {
        return part.value;
      }
    }
    return "";
  }, [formattedPrice]);

  const priceDecimal = useMemo(() => {
    if (!isEmptyObject(formattedPrice)) {
      const part = formattedPrice.find((i) =>
        equalsIgnoringCase("decimal", i.type)
      );
      if (!isEmptyObject(part)) {
        return part.value;
      }
    }
    return "";
  }, [formattedPrice]);

  const priceFraction = useMemo(() => {
    if (!isEmptyObject(formattedPrice)) {
      const part = formattedPrice.find((i) =>
        equalsIgnoringCase("fraction", i.type)
      );
      if (!isEmptyObject(part)) {
        return part.value;
      }
    }
    return "";
  }, [formattedPrice]);

  const priceCurrency = useMemo(() => {
    if (!isEmptyObject(formattedPrice)) {
      const part = formattedPrice.find((i) =>
        equalsIgnoringCase("currency", i.type)
      );
      if (!isEmptyObject(part)) {
        return part.value;
      }
    }
    return "";
  }, [formattedPrice]);

  const titlePrice = useMemo(() => {
    return t("plans.pro.price.title", {
      integer: priceInteger,
      decimal: priceDecimal,
      fraction: priceFraction,
      currency: priceCurrency,
    });
  }, [priceInteger, priceDecimal, priceFraction, priceCurrency, t]);

  const goProLabel = useMemo(() => {
    return t("button.go_pro");
  }, [t]);

  const features = useMemo(() => {
    const features = t("plans.pro.features");
    if (!isEmptyObject(features)) {
      return Object.values(features);
    }
    return [];
  }, [t]);

  const onRegisterHandler = useCallback(
    (evt) => {
      showRegister && onRegister && onRegister(evt);
    },
    [showRegister, onRegister]
  );

  const onChangeHandler = useCallback(
    (evt) => {
      showChange && onChange && onChange(evt);
    },
    [showChange, onChange]
  );

  return (
    <div className="pro-plan-card__container dark">
      {loading && (
        <div className="pro-plan-card__loading">
          <Loading />
        </div>
      )}
      <div className="pro-plan-card__content">
        <div className="pro-plan-card__header__container">
          <div className="pro-plan-card__header__content">
            <span className="plan-icon icon icon-up-round-arrow">
              <span className="path1"></span>
              <span className="path2"></span>
            </span>
            <span className="plan-title" title={title}>
              {title}
            </span>
            {isEmptyObject(formattedPrice) ? (
              <div className="plan-price"></div>
            ) : (
              <Trans
                i18nKey={"plans.pro.price.template"}
                components={{
                  container: (
                    <div className="plan-price" title={titlePrice}></div>
                  ),
                  integer: <span className="plan-price-integer-value"></span>,
                  fraction: <span className="plan-price-fraction-value"></span>,
                  taxes: <span className="plan-price-taxes"></span>,
                  currency: <span className="plan-price-currency"></span>,
                }}
                values={{
                  integer: priceInteger,
                  fraction: priceFraction,
                  currency: priceCurrency,
                  taxes: t("plans.pro.price.taxes"),
                }}
              />
            )}
            <div className="plan-button">
              {showRegister && (
                <Button
                  text
                  secondary
                  disabled={loading}
                  onClick={(evt) => onRegisterHandler(evt)}
                >
                  <span className="label" title={goProLabel}>
                    {goProLabel}
                  </span>
                </Button>
              )}
              {showChange && (
                <Button
                  text
                  secondary
                  disabled={loading}
                  onClick={(evt) => onChangeHandler(evt)}
                >
                  <span className="label" title={goProLabel}>
                    {goProLabel}
                  </span>
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="pro-plan-card__body__container">
          <div className="pro-plan-card__body__content">
            {!isEmptyList(features) && (
              <div className="plan-feature__list">
                {features.map((feature, idx) => (
                  <div key={idx} className="plan-feature__item">
                    <span className="item-icon icon icon-check-alternate"></span>
                    <span className="item-text">{feature}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProPlanCard;
